/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector as useSelectorRedux } from 'react-redux';
import { isArray, compact } from 'lodash';
import Collapse from 'antd/lib/collapse';
import { urlQueryString } from '@azgaz/azgaz-ui/connect/lib/utils';

import { Select, Option, CarsFilters as AuiCarsFilters } from '@azgaz/azgaz-ui';

import {
  useSelector,
  selectors,
  utils,
  useDispatch,
  Ranges,
  FilterValueDefault,
  actions as connectActions,
  FilterValueRange,
} from '@lib/azgaz-ui/connect';

import {
  numberFormat,
  config,
  removeOfficeFromUrl,
  parseFilters,
  removeModelFromUrl,
} from '@/utils';
import {
  DEFAULT_GROUP_FILTER,
  DEFAULT_GROUP_FILTER_VALUES,
  DEFAULT_STATE_FILTER,
  DEFAULT_STATE_FILTER_VALUES,
} from '@/constants';
import { useAsyncAction, useBaseUrlChange } from '@/hooks';
import { getColorsCode, getDealerOffices } from '@/selectors';
import filtersConfig from '../../filtersConfig';

import { State } from '@/apps/appCreator/store';
import PriceSlider from '@/apps/appCreator/components/PriceSlider';
import CarFilterCollapse, {
  FilterCollapseKeys,
} from '@/apps/appCreator/components/CarFilterCollapse';
import CarFilterSelected from '@/apps/appCreator/components/CarFilterSelected';
import CheckboxList from '@/apps/appCreator/components/CheckboxList';

type CarsFiltersProps = {
  form: any;
  ranges: Ranges;
  uiHelper: utils.UiHelper;
  collapseActiveKey?: FilterCollapseKeys | FilterCollapseKeys[];
  showFilter: boolean;
  onChangeShow: (v: boolean) => void;
  onChangeActiveKey: (keys: string | string[]) => void;
};

type TooltipOverlay = (props: { value: number }) => JSX.Element;

const featureFlagFilteringByStatus = config.get('featureFlagFilteringByStatus');

const CarsFilters: FC<CarsFiltersProps> = ({
  form,
  uiHelper,
  ranges = {},
  collapseActiveKey,
  showFilter,
  onChangeActiveKey,
  onChangeShow,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectors.getFiltersValues);
  const isFiltersLoading = useSelector(selectors.getFiltersLoading);
  const colorsCode = useSelectorRedux(getColorsCode);

  const priceRange = uiHelper.findFilter(
    filtersConfig.price.path
  ) as FilterValueRange;

  const [price, setPrice] = useState<[number, number] | undefined>(undefined);

  const [resetFilters] = useAsyncAction(uiHelper.resetFilters);
  const changeBaseUrl = useBaseUrlChange({
    callback: removeModelFromUrl,
  });

  const { resetFields, getFieldProps } = form;

  const selectedFilters = uiHelper.getAllFields();
  const dealerOffices = useSelectorRedux((state: State) =>
    getDealerOffices(state, selectedFilters.city)
  );

  const [
    onPriceChange,
    onEngineChange,
    onTransmissionChange,
    onDriveChange,
    onGradeChange,
    onColorChange,
    onModelChange,
    onOfficeChange,
    // onStateChange,
    onGroupChange,
  ] = uiHelper.createOnChange([
    filtersConfig.price,
    filtersConfig.engine,
    filtersConfig.transmission,
    filtersConfig.drive,
    filtersConfig.grade,
    filtersConfig.color,
    filtersConfig.model,
    filtersConfig.offices,
    filtersConfig.group,
  ]);

  const tooltipOverlay: TooltipOverlay = ({ value }) => (
    <>{numberFormat(value, 0, 3, ' ')}</>
  );

  const {
    price: priceFromQuery,
    // state: stateFromQuery,
    group: groupFormQuery,
  } = urlQueryString.get();

  const isExistPriceQuery = !!priceFromQuery;

  // const initStateValues = useMemo(() => {
  //   const resolvedStateQueryValues = isArray(stateFromQuery)
  //     ? stateFromQuery
  //     : compact([stateFromQuery]);
  //   return !resolvedStateQueryValues.length
  //     ? DEFAULT_STATE_FILTER
  //     : resolvedStateQueryValues;
  // }, [stateFromQuery]);

  const initGroupValues = useMemo(() => {
    const resolvedGroupQueryValues = isArray(groupFormQuery)
      ? groupFormQuery
      : compact([groupFormQuery]);
    return !resolvedGroupQueryValues.length
      ? DEFAULT_GROUP_FILTER
      : resolvedGroupQueryValues;
  }, [groupFormQuery]);

  const cityValues = uiHelper.findFilter(filtersConfig.city.name) as
    | FilterValueDefault
    | undefined;
  const showCityFilters = cityValues && cityValues.values.length > 1;

  const showOfficeFilter =
    (selectedFilters.city && dealerOffices && dealerOffices.length > 1) ||
    (cityValues &&
      cityValues.values.length <= 1 &&
      dealerOffices &&
      dealerOffices.length > 1);

  const officesFieldDisable =
    cityValues && cityValues.values.length > 1 && !selectedFilters.city;

  const onCityFieldChange = (value: string) => {
    dispatch(
      connectActions.postQueries({
        ...uiHelper.getAllFields(),
        [filtersConfig.city.id]: value,
      })
    );
    resetFields(['offices']);
    removeOfficeFromUrl();
  };

  const onModelFieldChange = useCallback(
    value => {
      onModelChange(value);
      changeBaseUrl(value);
    },
    [onModelChange]
  );

  useEffect(() => {
    if (priceRange && !price) {
      setPrice([priceRange.minValue, priceRange.maxValue]);
    }
  }, [priceRange, price]);

  const onFiltersReset = useCallback(() => {
    changeBaseUrl('');
    resetFilters();
    removeOfficeFromUrl();
  }, [priceRange]);

  const isShowAdditionalFilters = useMemo(
    () =>
      !![
        ...(typeof collapseActiveKey === 'string'
          ? [collapseActiveKey]
          : collapseActiveKey ?? []),
      ].find(key => key === FilterCollapseKeys.ADDITIONAL_FILTERS),
    [collapseActiveKey]
  );

  return (
    <AuiCarsFilters
      title={config.get('carsTitle')}
      isLoading={isFiltersLoading}
      onFiltersReset={onFiltersReset}
      show={showFilter}
      onClickShow={onChangeShow as any}
    >
      <CarFilterSelected
        colors={colorsCode[selectedFilters[filtersConfig.color.id]?.trim()]}
        {...parseFilters({
          baseFilterIds: [
            filtersConfig.model.id,
            filtersConfig.engine.id,
            filtersConfig.transmission.id,
            filtersConfig.drive.id,
            filtersConfig.grade.id,
            filtersConfig.price.id,
          ],
          selectedFilters,
          location: {
            dealerOffice: dealerOffices?.find(
              v => v.id === selectedFilters[filtersConfig.offices.id]?.[0]
            )?.name,
          },
        })}
      />
      {filters?.length && Object.keys(ranges).length && (
        <>
          <div className="row">
            <div className="col-sm-6 col-md-4 mb-4">
              <Select
                title="Модель"
                placeholder="Все"
                allowClear
                optionLabelProp="children"
                getPopupContainer={(trigger: any) => trigger.parentElement}
                showSearch={false}
                {...getFieldProps(filtersConfig.model.name, {
                  onChange: onModelFieldChange,
                  initialValue: config.get('model'),
                })}
              >
                {uiHelper
                  .getDefaultFilter(filtersConfig.model.path)
                  .map(({ title, value }) => (
                    <Option key={value}>{title}</Option>
                  ))}
              </Select>
            </div>
            <div className="col-sm-6 col-md-4 mb-4">
              <Select
                title="Комплектация"
                placeholder="Все"
                allowClear
                optionLabelProp="children"
                getPopupContainer={(trigger: any) => trigger.parentElement}
                showSearch={false}
                {...getFieldProps(filtersConfig.grade.name, {
                  onChange: (value: string | undefined) => {
                    if (!value) {
                      changeBaseUrl(''); // сбрасываем модель из урла
                    }
                    onGradeChange(value);
                  },
                })}
              >
                {uiHelper
                  .getDefaultFilter(filtersConfig.grade.path)
                  .map(({ title, value }) => (
                    <Option key={value}>{title}</Option>
                  ))}
              </Select>
            </div>
            {showCityFilters && (
              <div className="col-sm-6 col-md-4 mb-4">
                <Select
                  title="Город"
                  placeholder="Все"
                  allowClear
                  optionLabelProp="children"
                  getPopupContainer={(trigger: any) => trigger.parentElement}
                  showSearch={false}
                  {...getFieldProps(filtersConfig.city.name, {
                    onChange: onCityFieldChange,
                  })}
                >
                  {uiHelper
                    .getDefaultFilter(filtersConfig.city.path)
                    .map(({ title, value }) => (
                      <Option key={value}>{title}</Option>
                    ))}
                </Select>
              </div>
            )}
          </div>
          <div className="row">
            <div
              className={`col-sm-${featureFlagFilteringByStatus ? 8 : 12} mb-4`}
            >
              <PriceSlider
                isRange={isExistPriceQuery}
                title="стоимость, руб."
                space={12}
                spade={8}
                tooltipOverlay={tooltipOverlay}
                min={priceRange?.minValue}
                max={priceRange?.maxValue}
                {...getFieldProps(filtersConfig.price.name, {
                  onChange: onPriceChange,
                  initialValue: [priceRange?.minValue, priceRange?.maxValue],
                })}
              />
            </div>
            {featureFlagFilteringByStatus && (
              // <div className="col-sm-4 mb-4">
              //   <CheckboxList
              //     title="Статус"
              //     items={DEFAULT_STATE_FILTER_VALUES}
              //     {...getFieldProps(filtersConfig.state.name, {
              //       onChange: onStateChange,
              //       initialValue: initStateValues,
              //     })}
              //   />
              // </div>
              <div className="col-sm-4 mb-4">
                <CheckboxList
                  title="Статус"
                  items={DEFAULT_GROUP_FILTER_VALUES}
                  {...getFieldProps(filtersConfig.group.name, {
                    onChange: onGroupChange,
                    initialValue: initGroupValues,
                  })}
                />
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-sm-12 mb-4">
              <CarFilterCollapse
                activeKey={collapseActiveKey}
                onChange={onChangeActiveKey}
              >
                <Collapse.Panel
                  key={FilterCollapseKeys.ADDITIONAL_FILTERS}
                  header={
                    isShowAdditionalFilters
                      ? 'Скрыть дополнительные параметры'
                      : 'Показать дополнительные параметры'
                  }
                >
                  <div className="row">
                    <div className="col-sm-6 col-md-4 mb-4">
                      <Select
                        title="Двигатель"
                        placeholder="Все"
                        allowClear
                        optionLabelProp="children"
                        getPopupContainer={(trigger: any) =>
                          trigger.parentElement
                        }
                        showSearch={false}
                        {...getFieldProps(filtersConfig.engine.name, {
                          onChange: onEngineChange,
                        })}
                      >
                        {uiHelper
                          .getDefaultFilter(filtersConfig.engine.path)
                          .map(({ title, value }) => (
                            <Option key={value}>{title}</Option>
                          ))}
                      </Select>
                    </div>
                    <div className="col-sm-6 col-md-4 mb-4">
                      <Select
                        title="Трансмиссия"
                        placeholder="Все"
                        allowClear
                        optionLabelProp="children"
                        showSearch={false}
                        getPopupContainer={(trigger: any) =>
                          trigger.parentElement
                        }
                        {...getFieldProps(filtersConfig.transmission.name, {
                          onChange: onTransmissionChange,
                        })}
                      >
                        {uiHelper
                          .getDefaultFilter(filtersConfig.transmission.path)
                          .map(({ title, value }) => (
                            <Option key={value}>{title}</Option>
                          ))}
                      </Select>
                    </div>
                    <div className="col-sm-6 col-md-4 mb-4">
                      <Select
                        title="Привод"
                        placeholder="Все"
                        allowClear
                        optionLabelProp="children"
                        showSearch={false}
                        getPopupContainer={(trigger: any) =>
                          trigger.parentElement
                        }
                        {...getFieldProps(filtersConfig.drive.name, {
                          onChange: onDriveChange,
                        })}
                      >
                        {uiHelper
                          .getDefaultFilter(filtersConfig.drive.path)
                          .map(({ title, value }) => (
                            <Option key={value}>{title}</Option>
                          ))}
                      </Select>
                    </div>
                    <div className="col-sm-6 col-md-4 mb-4">
                      <Select
                        title="Цвет"
                        placeholder="Все"
                        allowClear
                        optionLabelProp="children"
                        showSearch={false}
                        getPopupContainer={(trigger: any) =>
                          trigger.parentElement
                        }
                        {...getFieldProps(filtersConfig.color.name, {
                          onChange: onColorChange,
                        })}
                      >
                        {uiHelper
                          .getDefaultFilter(filtersConfig.color.path, {
                            makeTitle: ({ count, id }) =>
                              utils.UiHelper.defaultFilterMakeTitle(id, count),
                          })
                          .map(({ title, value }) => (
                            <Option key={value}>
                              <div className="d-flex justify-content-between">
                                <div>{title}</div>
                                <div className="rs-select-color">
                                  <div
                                    style={{
                                      backgroundColor:
                                        colorsCode[value?.trim()]?.topHexCode,
                                    }}
                                  />
                                  <div
                                    style={{
                                      backgroundColor:
                                        colorsCode[value?.trim()]?.bodyHexCode,
                                    }}
                                  />
                                </div>
                              </div>
                            </Option>
                          ))}
                      </Select>
                    </div>
                    {showOfficeFilter && (
                      <div className="col-sm-6 col-md-4 mb-4">
                        <Select
                          title="Дилерский центр"
                          placeholder="Все"
                          allowClear
                          optionLabelProp="children"
                          showSearch={false}
                          {...getFieldProps('offices', {
                            onChange: onOfficeChange,
                          })}
                          getPopupContainer={(trigger: any) =>
                            trigger.parentElement
                          }
                          disabled={officesFieldDisable}
                        >
                          {uiHelper
                            .getDefaultFilter(filtersConfig.offices.path)
                            .filter(({ value }) => !!value)
                            .map(({ value }) => {
                              // TODO сопоставлять офисы с их именами в более подходящем месте
                              const office = dealerOffices?.find(
                                ({ id }) => id === value
                              );

                              return (
                                <Option key={value}>{office?.name}</Option>
                              );
                            })}
                        </Select>
                      </div>
                    )}
                  </div>
                </Collapse.Panel>
              </CarFilterCollapse>
            </div>
          </div>
        </>
      )}
    </AuiCarsFilters>
  );
};

export default utils.withForm(CarsFilters);
